<template>
  <div v-if="isOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-lg w-full max-w-md mx-4 relative">
      <!-- Close button -->
      <button 
        @click="closeModal" 
        class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <i class="fas fa-times text-xl"></i>
      </button>

      <!-- Header -->
      <div class="p-6 pb-4">
        <h2 class="text-2xl font-bold text-gray-800 text-center mb-2">
          {{ isLogin ? 'Welcome Back!' : 'Join Captain Leo!' }}
        </h2>
        <p class="text-gray-600 text-center">
          {{ isLogin ? 'Sign in to continue your adventures' : 'Start exploring the world' }}
        </p>
      </div>

      <!-- Form -->
      <div class="px-6 pb-6">
        <form @submit.prevent="handleSubmit">
          <!-- Username field -->
          <div class="mb-4">
            <label for="name" class="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              v-model="form.name"
              type="text"
              id="name"
              required
              :pattern="isLogin ? null : '[a-z0-9-]+'"
              :title="isLogin ? null : 'Username can only contain lowercase letters, numbers, and hyphens'"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              :placeholder="isLogin ? 'Enter your username' : 'Enter your username (lowercase, numbers, dashes)'"
              @input="validateUsername"
            />
            <p v-if="!isLogin && usernameError" class="text-xs text-red-500 mt-1">
              {{ usernameError }}
            </p>
            <p v-if="!isLogin && !usernameError" class="text-xs text-gray-500 mt-1">
              Username: lowercase letters, numbers, and dashes only (3-30 characters)
            </p>
          </div>

          <!-- Username suggestions (register only) -->
          <div v-if="!isLogin && usernameSuggestions.length > 0" class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Need inspiration? Try these:
            </label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="suggestion in usernameSuggestions"
                :key="suggestion"
                type="button"
                @click="form.name = suggestion; validateUsername()"
                class="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
              >
                {{ suggestion }}
              </button>
              <button
                type="button"
                @click="fetchUsernameSuggestions"
                class="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
              >
                <i class="fas fa-sync-alt mr-1"></i> More
              </button>
            </div>
          </div>

          <!-- Email field (register only) -->
          <div v-if="!isLogin" class="mb-4">
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              v-model="form.email"
              type="email"
              id="email"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              placeholder="Enter your email"
            />
          </div>

          <!-- Password field -->
          <div class="mb-4">
            <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              v-model="form.password"
              type="password"
              id="password"
              required
              :minlength="isLogin ? 1 : 6"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              placeholder="Enter your password"
            />
            <p v-if="!isLogin" class="text-xs text-gray-500 mt-1">
              Password must be at least 6 characters long
            </p>
          </div>

          <!-- Error message -->
          <div v-if="error" class="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {{ error }}
          </div>

          <!-- Submit button -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isLoading" class="flex items-center justify-center">
              <i class="fas fa-spinner fa-spin mr-2"></i>
              {{ isLogin ? 'Signing in...' : 'Creating account...' }}
            </span>
            <span v-else>
              {{ isLogin ? 'Sign In' : 'Create Account' }}
            </span>
          </button>
        </form>

        <!-- Toggle between login/register -->
        <div class="mt-4 text-center">
          <p class="text-gray-600">
            {{ isLogin ? "Don't have an account?" : "Already have an account?" }}
            <button
              @click="toggleMode"
              class="text-blue-600 hover:text-blue-800 font-medium ml-1"
            >
              {{ isLogin ? 'Sign up' : 'Sign in' }}
            </button>
          </p>
        </div>

        <!-- SSO providers -->
        <div class="mt-6 pt-4 border-t border-gray-200">
          <p class="text-xs text-gray-500 text-center mb-3">Or continue with</p>
          <div class="flex flex-col gap-2">
            <SsoButton provider="google" @success="handleSsoSuccess" @error="handleSsoError" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import SsoButton from '@/components/SsoButton.vue'

export default {
  name: 'AuthModal',
  components: { SsoButton },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'login' // 'login' or 'register'
    }
  },
  emits: ['close', 'success'],
  data() {
    return {
      isLogin: this.mode === 'login',
      form: {
        name: '',
        email: '',
        password: ''
      },
      usernameSuggestions: [],
      usernameError: null
    }
  },
  computed: {
    ...mapState('auth', ['isLoading', 'error'])
  },
  watch: {
    mode(newMode) {
      this.isLogin = newMode === 'login'
      this.resetForm()
      // Fetch username suggestions when switching to register mode
      if (!this.isLogin && this.isOpen) {
        this.fetchUsernameSuggestions()
      }
    },
    isOpen(newValue) {
      if (newValue) {
        this.resetForm()
        // Fetch username suggestions when opening register mode
        // Need to use nextTick to ensure isLogin is set from props
        this.$nextTick(() => {
          if (!this.isLogin) {
            this.fetchUsernameSuggestions()
          }
        })
      }
    }
  },
  methods: {
    ...mapActions('auth', ['login', 'register']),
    
    closeModal() {
      this.$emit('close')
    },
    
    toggleMode() {
      this.isLogin = !this.isLogin
      this.resetForm()
    },
    
    resetForm() {
      this.form = {
        name: '',
        email: '',
        password: ''
      }
      this.usernameError = null
      this.usernameSuggestions = []
    },

    validateUsername() {
      if (this.isLogin) return // Skip validation for login

      const username = this.form.name.trim()

      if (!username) {
        this.usernameError = null
        return
      }

      // Check format: only lowercase letters, numbers, and dashes
      if (!/^[a-z0-9-]+$/.test(username)) {
        this.usernameError = 'Only lowercase letters, numbers, and dashes allowed'
        return
      }

      // Check length
      if (username.length < 3) {
        this.usernameError = 'Username must be at least 3 characters'
        return
      }

      if (username.length > 30) {
        this.usernameError = 'Username must be 30 characters or less'
        return
      }

      this.usernameError = null
    },

    async fetchUsernameSuggestions() {
      try {
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000'
        const response = await fetch(`${apiUrl}/api/auth/username-suggestions`)
        const data = await response.json()

        if (data.suggestions) {
          this.usernameSuggestions = data.suggestions
        }
      } catch (error) {
        console.error('Error fetching username suggestions:', error)
      }
    },

    handleSsoSuccess() {
      this.$emit('success')
      this.closeModal()
    },

    handleSsoError(message) {
      // Show "coming soon" messages as info, not as alarming errors
      if (message && message.toLowerCase().includes('coming soon')) {
        alert(message)
      } else if (message && !message.toLowerCase().includes('popup_closed')) {
        // Suppress "user closed popup" — that's intentional, not an error
        this.$store.commit('auth/SET_ERROR', message)
      }
    },

    async handleSubmit() {
      try {
        let result

        if (this.isLogin) {
          // Login with username instead of email
          result = await this.login({
            username: this.form.name,
            password: this.form.password
          })
        } else {
          result = await this.register({
            name: this.form.name,
            email: this.form.email,
            password: this.form.password
          })
        }

        if (result.success) {
          this.$emit('success')
          this.closeModal()
        }
      } catch (error) {
        console.error('Auth error:', error)
      }
    }
  }
}
</script>